import type { DisciplineAllocationLog, DisciplineAllocationTutor } from './interfaces'

import { useListsStore } from '@shared/model'
import { useAdminRoutes } from '@shared/lib/routes'

import { DisciplineAllocationStatus } from './enums'
import { useDisciplineScheduleStore } from './schedule/store'
import { useDisciplineScheduleContext } from './discipline-schedule-context.hook'
import {
  findAllocationTutor,
  formatScheduleDay,
  getAllocationLabel,
  getAllocationStatus,
  getAllocationTooltip,
  getAllocationTutorName,
} from './discipline-schedule'

export interface DisciplineScheduleTutorRow {
  key: string
  log: DisciplineAllocationLog
  tutor: DisciplineAllocationTutor
  tutorName: string
  tutorUrl: string | null
  paidLabel: string
  slots: string[]
  status: DisciplineAllocationStatus
  label: string
  tooltip: string
}

interface ModalDisciplineScheduleAssignReturn {
  refRows: ComputedRef<DisciplineScheduleTutorRow[]>
  refIsLoading: ComputedRef<boolean>
  refIsDisabled: ComputedRef<boolean>
  isChosen: (row: DisciplineScheduleTutorRow) => boolean
  choose: (row: DisciplineScheduleTutorRow) => void
  close: () => void
  back: () => void
  confirm: () => void
}

/** The run's candidates; a tutor that is no longer assignable comes without a tutor row and is not offered. */
export const useModalDisciplineScheduleAssign = (): ModalDisciplineScheduleAssignReturn => {
  const store = useDisciplineScheduleStore()
  const listsStore = useListsStore()
  const { close } = useDisciplineScheduleContext()

  const refRows = computed<DisciplineScheduleTutorRow[]>(() => (store.getAllocationLogs ?? []).flatMap((log, index) => {
    const tutor = findAllocationTutor(log, store.getAllocationTutors)
    if (!tutor) return []

    const paidLabel = tutor.paid_label ? listsStore.getLists?.tutor.paid_label[tutor.paid_label] : null

    return [{
      key: `${tutor.id}-${index}`,
      log,
      tutor,
      tutorName: getAllocationTutorName(tutor),
      tutorUrl: useAdminRoutes('admin.tutor.show', { tutor_id: tutor.id }),
      paidLabel: `(${paidLabel || '-'})`,
      slots: log.slot_pair.map(formatScheduleDay),
      status: getAllocationStatus(log.availability),
      label: getAllocationLabel(log),
      tooltip: getAllocationTooltip(log),
    }]
  }))

  const refIsLoading = computed<boolean>(() => store.getAllocationLogs === null)
  const refIsDisabled = computed<boolean>(() => !store.getChosenLog)

  const isChosen = (row: DisciplineScheduleTutorRow): boolean => toRaw(store.getChosenLog) === toRaw(row.log)

  const choose = (row: DisciplineScheduleTutorRow): void => {
    store.setChosenLog(row.log)
  }

  const back = (): void => {
    store.resetAllocation()
    store.previousStep()
  }

  return {
    refRows,
    refIsLoading,
    refIsDisabled,
    isChosen,
    choose,
    close,
    back,
    confirm: () => store.nextStep(),
  }
}
